<template>
  <responsive-drawer-layout>
    <template v-slot:content>
      <div v-if="!dataLoaded" class="d-flex justify-center align-center">
        <dots-loader></dots-loader>
      </div>
      <div v-else class="pt-1 inherit-height scroll-container d-flex flex-column" style="height: 101%">
        <div class="detail-view-container">
          <v-card class="video-detail-card pb-8" flat>
            <div class="video-container-wrapper">
              <video-player
                objectFit="contain"
                controls
                :src="videoUrl"
                :posterUrl="thumbnailUrl"
                :playOnMouseOver="false"
                :autoplay="true"
                :muted="isMuted"
                @volumechange="handleVolumeChange"
                class="video-player-element"
              ></video-player>
            </div>

            <div v-if="video._group" class="mx-6 mt-4">
              <v-chip :color="chipColors" small depressed class="px-3 text-caption">
                {{ video._group.name }}
              </v-chip>
            </div>

            <div class="mx-6 mt-3 d-flex align-center justify-space-between" style="min-height: 40px;">
              <div class="subtitle-4 grey--text text--darken-1 flex-shrink-0">
                Shared on {{ formatDate(video.anzeige_von) }}
              </div>

              <subtitle-2
                class="flex-grow-1"
                style="margin-top: 0 !important; margin-left: 0 !important;"
                :item="video"
                :display-view-counter="moduleConfig.displayViewCounter"
                :downloadBtn="showDownloadBtn"
                :downloadFileTitle="mx_videoDownloadName(video)"
                @onDownload="mx_downloadFile(video)"
              ></subtitle-2>
            </div>

            <div class="mx-6 mt-2">
              <div class="op-text-xl" @click.stop="show">
                {{ video.title }}
              </div>
            </div>

            <div v-if="video.description" class="mx-6 mt-6 op-text">
              {{ video.description }}
            </div>

          </v-card>
        </div>
      </div>
    </template>
  </responsive-drawer-layout>
</template>

<script>
import ResponsiveDrawerLayout from '@/layouts/ResponsiveDrawerLayout';
import VideoPlayer from '@/components/VideoPlayer';
import dateTimeMixin from '@/mixins/dateTimeMixin.js';
import dateUtils from '@/common/dateUtils';
import common from '@/mixins/common';
import configMixin from '@/mixins/configMixin';
import videoMixin from './videoMixin';
import { mapState } from 'vuex';
import DotsLoader from '@/components/spinner/Dots';
import readService from '@/services/readService';
import Subtitle2 from '../../components/Subtitle2';

export default {
  mixins: [common, dateTimeMixin, configMixin, videoMixin],
  props: {
    videoHeight: { type: Number, default: 200 },
  },
  components: { DotsLoader, ResponsiveDrawerLayout, VideoPlayer, Subtitle2 },
  data() {
    return {
      isMuted: localStorage.getItem('video_player_muted') === 'true',
    };
  },
  mounted() {
    this.markAsRead();
  },
  watch: {
    video(newVal) {
      if (newVal) {
        this.markAsRead();
      }
    }
  },
  computed: {
    ...mapState('videos', ['videosById', 'groups', 'dataLoaded']),
    moduleConfig() {
      return this.getModuleConfig('Video', this.$route.params.instance);
    },
    video() {
      if (this.dataLoaded) {
        return this.videosById[this.$route.params.videoId];
      }
      return null;
    },
    thumbnailUrl() {
      return this.mx_thumbnailUrl(this.video);
    },
    videoUrl() {
      return this.mx_videoUrl(this.video);
    },
    streamedTime() {
      if (!this.video || !this.video.anzeige_von) return '';
          
        // Changing this return value will automatically update the text in the top-left header
        // From "Shared 7 hours ago" to "Shared on February 1, 2026"
        return `Shared on ${this.formatDate(this.video.anzeige_von)}`;
    },
    isRead() {
      return readService.isRead(this.video);
    },
    videoDownloadName() {
      return this.video ? this.mx_videoDownloadName(this.video) : null;
    },
    showDownloadBtn() {
      if (this.video.downloadable !== undefined) {
        return Boolean(this.video.downloadable);
      }

      return true;
    },
  },
  methods: {
    markAsRead() {
      if (this.video) {
        // this.$store.dispatch(`sendbox/markAsRead`, { item: this.video });
        let sendboxTitle = this.$t('action.video.seen.msg', { object: this.video.title });
        let snackbarText = this.$t('action.video.read');
        this.$store.dispatch(
          `sendbox/markAsRead2`,
          {
            item: this.video,
            sendboxTitle,
            snackbarText,
            immediate: true,
          },
          { root: true }
        );
      }
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    toggleReadState() {
      if (this.video) {
        let action = readService.isRead(this.video) ? 'markAsUnread2' : 'markAsRead2';
        let sendboxTitle = this.$t('action.video.seen.msg', { object: this.video.title });
        let snackbarText = this.$t('action.video.read');
        this.$store.dispatch(
          `sendbox/${action}`,
          {
            item: this.video,
            sendboxTitle,
            snackbarText,
            immediate: true,
          },
          { root: true }
        );
        // this.$store.dispatch(`sendbox/${action}`, { item: this.video, immediate: true });
      }
    },
    handleVolumeChange(event) {
      this.isMuted = event.target.muted;
      localStorage.setItem('video_player_muted', this.isMuted.toString());
    }
  },
};
</script>
<style scoped>
.detail-view-container {
  width: 100%;
  display: flex;
  flex-grow: 1;
  min-height: 100%;
  justify-content: center;
  /* Top: 0 | Right: 16px | Bottom: 0px | Left: 16px */
  padding: 0 16px 0px 16px;
  margin: 0;
}

.video-detail-card {
  width: 100%;
  height: 100%;
  max-width: 1296px;
  background-color: transparent !important;
  margin: 0;
  border-left: 1px solid #e0e0e0 !important;
  border-right: 1px solid #e0e0e0 !important;
  border-top: none !important;
  border-bottom: none !important;
}

.video-container-wrapper {
  background-color: #f5f5f5;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-height: 95vh;
  overflow: hidden;
}

.video-player-element {
  width: 100%;
  height: 100%;
  max-height: 95vh;
}

.op-text-xl {
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  font-size: 32px;
}
</style>
